import { v4 as uuidv4 } from 'uuid';
import { HtmlWidgetKey, isHtmlWidgetKey } from '@inference.engine/widgets/web';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useUIStore } from '@/app/store/ui';
import { logger } from '@/lib/utils/logger';
import { initializeWidgetProcessorMap } from './widget-action.dispatcher';
import {
  AgentWidgetHistory,
  WidgetActions,
  WidgetState,
  WidgetVersion,
  WidgetWithMeta,
} from './widget.store.types';

const initialState: WidgetState = {
  widget: null,
  loading: false,
  error: null,
  renderMode: 'chat',
  agentHistories: {},
  widgetMessages: {},
  unsavedWidgetEdits: {},
};

// Type guard for widget data with items array
function hasItemsArray(data: unknown): data is { items: unknown[] } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'items' in data &&
    Array.isArray((data as { items: unknown[] }).items)
  );
}

// Parse a `createdAt` value (Date | ISO string | epoch number) to epoch ms, or null when absent/invalid.
function parseCreatedAt(createdAt: unknown): number | null {
  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }
  if (typeof createdAt === 'string') {
    const parsed = new Date(createdAt);
    if (!isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }
  if (typeof createdAt === 'number') {
    return createdAt;
  }
  return null;
}

// Resolve the version timestamp, preferring the REAL artifact creation time carried on the widget
// envelope (`widget.createdAt`, set by `useChatHistory` on rehydration) so historical versions show
// their true time. Falls back to a `createdAt` embedded in the widget data, then — for live-stream /
// manual-save versions that carry no createdAt — to the current time (correct: they're created now).
function extractWidgetTimestamp(widget: WidgetWithMeta): number {
  const envelopeTs = parseCreatedAt(widget.createdAt);
  if (envelopeTs !== null) {
    return envelopeTs;
  }

  const data = widget.data;
  if (typeof data === 'object' && data !== null && 'createdAt' in data) {
    const dataTs = parseCreatedAt((data as { createdAt: unknown }).createdAt);
    if (dataTs !== null) {
      return dataTs;
    }
  }

  return Date.now();
}

/**
 * Widget store using Zustand for optimal performance
 * Replaces the React Context-based WidgetPanelContext and eliminates JSONPath-based updates
 */

export const useWidgetStore = create<WidgetState & WidgetActions>()(
  devtools(
    (set, get) => {
      const widgetProcessorMap = initializeWidgetProcessorMap(set);

      return {
        ...initialState,

        setWidget: (widget: WidgetWithMeta | null) => {
          set({
            widget,
            loading: false,
            error: null,
          });
        },

        setWidgetMessages: (
          setter: (current: Record<string, string[]>) => Record<string, string[]>,
        ) => {
          set((prev) => ({
            ...prev,
            widgetMessages: setter(prev.widgetMessages),
          }));
        },

        setRenderMode: (mode) => set({ renderMode: mode }),

        clearWidget: () => {
          set({
            widget: null,
            loading: false,
            error: null,
          });
        },

        // Lives here instead of setAgentById to avoid clearing widgets during programmatic
        // agent switches (e.g., Generate Brief flow). Only called from user-initiated switch points.
        clearWidgetPanel: () => {
          const { widget, clearWidget } = get();
          if (widget) {
            clearWidget();
            const uiState = useUIStore.getState();
            if (uiState.sidePanelType === 'widget') {
              uiState.closeSidePanel();
            }
          }
        },

        clearError: () => {
          set({
            error: null,
          });
        },

        setLoading: (loading: boolean) => {
          set((prev) => ({ ...prev, loading }));
        },

        processWidget: (messageId: string, widget: WidgetWithMeta) => {
          // Each agent-made widget has its own key, so they share the processor of their kind.
          const processor = widgetProcessorMap.get(
            isHtmlWidgetKey(widget.key) ? HtmlWidgetKey : widget.key,
          );
          if (!processor) {
            logger.debug(`No processor found for widget key: ${widget.key}`);
            return;
          }

          processor(messageId, widget);
        },

        reset: () => set(initialState),

        setWidgetUnsavedEdits: (source: string, dirty: boolean) => {
          set((prev) => {
            // A source is dirty iff its key is PRESENT — the map only ever holds `true`, so presence
            // is the whole state and `hasUnsavedWidgetEdits` stays a plain emptiness check. Bail out
            // when nothing changes, so a clean editor re-rendering doesn't mint a new map identity.
            if (source in prev.unsavedWidgetEdits === dirty) return prev;
            const next: Record<string, boolean> = Object.fromEntries(
              Object.entries(prev.unsavedWidgetEdits).filter(([key]) => key !== source),
            );
            if (dirty) next[source] = true;
            return { ...prev, unsavedWidgetEdits: next };
          });
        },

        hasUnsavedWidgetEdits: () => Object.keys(get().unsavedWidgetEdits).length > 0,

        // New widget versioning actions
        storeWidgetVersion: (
          messageId: string,
          agentId: string,
          widget: WidgetWithMeta,
          title?: string,
        ) => {
          set((prev) => {
            const versionId = uuidv4();
            // Prefer the artifact's real createdAt (widget envelope), then a data-embedded
            // createdAt, then Date.now() for freshly-created (live/manual-save) versions.
            const timestamp = extractWidgetTimestamp(widget);

            // Get existing agent history
            const currentHistory = prev.agentHistories[agentId];
            const version = widget.version
              ? widget.version
              : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                currentHistory
                ? currentHistory.versions.length + 1
                : 1;

            // Normalize widget data structure for consistent storage
            let normalizedData = widget.data;

            // Handle different widget action types for proper data accumulation
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (widget.data && typeof widget.data === 'object' && widget.data !== null) {
              // If widget has { item: {...} } structure (from addWidget), we need to merge with existing data
              if ('item' in widget.data && !('items' in widget.data)) {
                const itemData = (widget.data as { item: unknown }).item;

                // Check if we have existing widget data for this agent
                const existingWidget =
                  prev.widget && prev.widget.agentName === agentId ? prev.widget : null;
                const existingItems =
                  existingWidget && hasItemsArray(existingWidget.data)
                    ? existingWidget.data.items
                    : [];

                // Accumulate: add new item to existing items array
                normalizedData = {
                  items: [...existingItems, itemData],
                };
              }
              // If widget already has { items: [...] } structure (from createWidget), keep as-is
              else if (hasItemsArray(widget.data)) {
                normalizedData = widget.data;
              }
            }

            // Ensure the widget has the correct agentName (agentId) and normalized data for rendering
            const correctedWidget: WidgetWithMeta = {
              ...widget,
              agentName: agentId, // Use agentId instead of original agentName to ensure consistency
              data: normalizedData, // Use normalized data structure
            };

            const newVersion: WidgetVersion = {
              id: versionId,
              version,
              messageId,
              agentId,
              widget: correctedWidget, // Store with corrected agentName and normalized data
              timestamp,
              title,
            };

            // An agent-made inline widget lives in its reply; the panel keeps what it shows.
            const staysInline =
              isHtmlWidgetKey(widget.key) &&
              (widget.data as { placement?: unknown } | null)?.placement === 'inline';

            // Create updated history
            const updatedHistory: AgentWidgetHistory = {
              agentId,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              versions: currentHistory ? [...currentHistory.versions, newVersion] : [newVersion],
              // A new version becomes the active one, unless it stays inline
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- undefined for an agent's first widget
              activeVersionId: staysInline ? currentHistory?.activeVersionId : versionId,
            };

            // Update message mapping
            const currentMessageAgents = prev.widgetMessages[messageId] ?? [];
            const updatedMessageAgents = currentMessageAgents.includes(agentId)
              ? currentMessageAgents
              : [...currentMessageAgents, agentId];

            // Decide which widget to set as current
            // Priority: keep widget with valid items over empty widget
            let widgetToSet: WidgetWithMeta | null = staysInline ? prev.widget : correctedWidget;

            if (prev.widget && prev.widget.agentName === agentId) {
              // We have an existing widget for the same agent
              const currentHasItems =
                hasItemsArray(prev.widget.data) && prev.widget.data.items.length > 0;
              const newHasItems =
                hasItemsArray(correctedWidget.data) && correctedWidget.data.items.length > 0;

              // If current widget has items and new one doesn't, keep the current one
              if (currentHasItems && !newHasItems) {
                widgetToSet = prev.widget;
              }
            }

            return {
              ...prev,
              agentHistories: {
                ...prev.agentHistories,
                [agentId]: updatedHistory,
              },
              widgetMessages: {
                ...prev.widgetMessages,
                [messageId]: updatedMessageAgents,
              },
              widget: widgetToSet, // Use the widget with better data
            };
          });
        },

        setActiveVersion: (agentId: string, versionId: string) => {
          set((prev) => {
            const history = prev.agentHistories[agentId];
            const version = history.versions.find((v) => v.id === versionId);
            if (!version) {
              return prev;
            }

            // Ensure the restored widget has the correct agentName for rendering
            const correctedWidget: WidgetWithMeta = {
              ...version.widget,
              agentName: agentId, // Ensure agentName matches agentId for consistent rendering
              version: version.version, // Keep version number for reference
            };

            return {
              ...prev,
              agentHistories: {
                ...prev.agentHistories,
                [agentId]: {
                  ...history,
                  activeVersionId: versionId,
                },
              },
              widget: correctedWidget, // Set corrected widget as current
            };
          });
        },

        // Set the latest version as active (useful for ensuring latest is always active)
        setLatestVersionAsActive: (agentId: string) => {
          set((prev) => {
            const history = prev.agentHistories[agentId];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!history) {
              return prev;
            }
            if (history.versions.length === 0) {
              return prev;
            }

            const latestVersion = history.versions[history.versions.length - 1];
            const correctedWidget: WidgetWithMeta = {
              ...latestVersion.widget,
              agentName: agentId,
            };

            return {
              ...prev,
              agentHistories: {
                ...prev.agentHistories,
                [agentId]: {
                  ...history,
                  activeVersionId: latestVersion.id,
                },
              },
              widget: correctedWidget,
            };
          });
        },

        clearActiveVersion: (agentId: string) => {
          set((prev) => {
            const history = prev.agentHistories[agentId];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!history) {
              return prev;
            }
            return {
              ...prev,
              agentHistories: {
                ...prev.agentHistories,
                [agentId]: {
                  ...history,
                  activeVersionId: undefined,
                },
              },
            };
          });
        },

        // Switch active state to a specific agent and version, clearing all others
        switchToAgentVersion: (agentId: string, versionId: string) => {
          set((prev) => {
            // Clear active state from all agents first
            const updatedAgentHistories = { ...prev.agentHistories };

            Object.keys(updatedAgentHistories).forEach((otherAgentId) => {
              const otherHistory = updatedAgentHistories[otherAgentId];
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              if (otherHistory) {
                updatedAgentHistories[otherAgentId] = {
                  ...otherHistory,
                  activeVersionId: undefined,
                };
              }
            });

            // Set the specified agent and version as active
            const targetHistory = updatedAgentHistories[agentId];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (targetHistory) {
              const targetVersion = targetHistory.versions.find((v) => v.id === versionId);

              if (targetVersion) {
                const correctedWidget: WidgetWithMeta = {
                  ...targetVersion.widget,
                  agentName: agentId,
                };

                updatedAgentHistories[agentId] = {
                  ...targetHistory,
                  activeVersionId: versionId,
                };

                return {
                  ...prev,
                  agentHistories: updatedAgentHistories,
                  widget: correctedWidget,
                };
              }
            }

            return {
              ...prev,
              agentHistories: updatedAgentHistories,
            };
          });
        },

        getVersionsForMessage: (agentId: string, messageId: string, fallbackAgentId?: string) => {
          const { agentHistories } = get();

          const getLatestVersion = (
            history: AgentWidgetHistory | undefined,
          ): WidgetVersion | null => {
            if (!history) return null;

            const versionsForMessage = history.versions.filter(
              (v: WidgetVersion) => v.messageId === messageId,
            );
            if (versionsForMessage.length === 0) return null;

            // Return the most recent version (highest version number, then timestamp as tiebreaker)
            return versionsForMessage.reduce((latest, candidate) => {
              if (candidate.version !== latest.version) {
                return candidate.version > latest.version ? candidate : latest;
              }
              return candidate.timestamp > latest.timestamp ? candidate : latest;
            });
          };

          // Try primary agentId first
          const primaryHistory = agentHistories[agentId];
          const primaryVersion = getLatestVersion(primaryHistory);
          if (primaryVersion) {
            return { versions: [primaryVersion], resolvedAgentId: agentId };
          }

          // Fallback to secondary agentId if provided and different
          if (fallbackAgentId && fallbackAgentId !== agentId) {
            const fallbackHistory = agentHistories[fallbackAgentId];
            const fallbackVersion = getLatestVersion(fallbackHistory);
            if (fallbackVersion) {
              return { versions: [fallbackVersion], resolvedAgentId: fallbackAgentId };
            }
          }

          return { versions: [], resolvedAgentId: fallbackAgentId ?? agentId };
        },

        getActiveVersion: (agentId: string) => {
          const { agentHistories } = get();
          const history = agentHistories[agentId];

          // Return null if no history exists for this agent
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!history) {
            return null;
          }

          return (
            history.versions.find((v: WidgetVersion) => v.id === history.activeVersionId) ?? null
          );
        },

        hasWidgetForMessage: (messageId: string) => {
          const { widgetMessages } = get();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          return widgetMessages[messageId] && widgetMessages[messageId].length > 0;
        },
      };
    },
    { name: 'widget-store' },
  ),
);

// Performance-optimized selectors
export const useWidget = () => useWidgetStore((state) => state.widget);
export const useWidgetLoading = () => useWidgetStore((state) => state.loading);
export const useWidgetError = () => useWidgetStore((state) => state.error);
export const useWidgetActions = () =>
  useWidgetStore((state) => ({
    setWidget: state.setWidget,
    setWidgetMessages: state.setWidgetMessages,
    clearWidget: state.clearWidget,
    clearWidgetPanel: state.clearWidgetPanel,
    setLoading: state.setLoading,
    processWidget: state.processWidget,
    reset: state.reset,
    clearError: state.clearError,
  }));

// Compatibility selector that matches the original WidgetPanelContext interface
export const useWidgetPanel = () =>
  useWidgetStore((state) => ({
    widget: state.widget,
    loading: state.loading,
    agentHistories: state.agentHistories, // Added for versioning support
    setWidget: state.setWidget,
    clearWidget: state.clearWidget,
    setLoading: state.setLoading,
    processWidget: state.processWidget,
  }));

if (import.meta.env.DEV) {
  window.useWidgetStore = useWidgetStore;
}
